import React, { useState, useEffect, useRef } from "react";
import ReactECharts from "echarts-for-react";
import {
  Row,
  Col,
  Card,
  Statistic,
  Badge,
  Skeleton,
  Select,
  Space,
  Button,
  DatePicker,
  Radio,
  Drawer,
  Table,
  Typography,
  Tabs,
  Pagination,
  Divider,
  Form,
  Grid,
  Modal,
  Descriptions,
  Tag,
  theme,
} from "antd";
import { blue, green, red } from "@ant-design/colors";
import { FilterTwoTone } from "@ant-design/icons";
import { axiosClient } from "../../apiClient";

const { Title, Link } = Typography;
const { useBreakpoint } = Grid;
const { useToken } = theme;

const SellerStats = React.memo(() => {
  const screens = useBreakpoint();
  const [form] = Form.useForm();
  const { token } = useToken();
  const [mainTab, setMainTab] = useState("1");
  const [displayTab, setDisplayTab] = useState("week_date");
  const [displayMetric, setDisplayMetric] = useState("qty_orders");
  const [segmentedValue, setSegmentedValue] = useState(null);
  const [segmentedData, setSegmentedData] = useState(null);
  const [segmentedLoading, setSegmentedLoading] = useState(false);
  const [orderListModal, setOrderListModal] = useState(false);
  const [dateRanges, setDateRanges] = useState(null);

  const [aggregatedData, setAggregatedData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [buyerGraphData, setBuyerGraphData] = useState([]);
  const [highLightData, setHighLightData] = useState(null);

  const [filterValues, setFilterValues] = useState({
    customerOrder: "hasOrder",
    customerStatus: "all",
    abcCurve: "all",
    registerDate: "all",
  });

  const [graphType, setGraphType] = useState("line");
  const [analysisPeriod, setAnalysisPeriod] = useState("last7");
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [loading, setLoading] = useState(true);
  const [buyerLoading, setBuyerLoading] = useState(true);
  const [customRange, setCustomRange] = useState(null);

  const [filterDrawerVisible, setFilterDrawerVisible] = useState(false);

  const { Text } = Typography;
  const { RangePicker } = DatePicker;
  const { TabPane } = Tabs;

  useEffect(() => form.setFieldValue(filterValues), [filterValues]);

  const METRICS = [
    "qty_orders",
    "total_value",
    "mean_ticket",
    "activations",
    "order_mix",
    "price",
    "quantities",
  ];

  const labelToTitle = {
    RTB: "Prontos Para Comprar",
    RISK: "Em Risco",
    SERVED: "Atendidos",
    LOST: "Perdidos",
    above1Year: "Acima de 1 Ano",
    last12Months: "No Último Ano",
    last30Days: "Últimos 30 Dias",
    last7Days: "Últimos 7 Dias",
    today: "Hoje",
    pf: "Pessoa Física",
    pj: "Pessoa Juridica",
    seller: "Vendedor (Admin)",
    selleruser: "Vendedor",
    buyer: "Cliente",
  };

  const statsLabels = {
    qty_orders: "Número de Pedidos",
    total_value: "Faturamento",
    activations: "Clientes Positivados",
    mean_ticket: "Ticket Médio",
    order_mix: "Mix Médio",
    price: "Preço Médio",
    quantities: "Quantidade Média",
    qty_buyers: "Clientes",
  };

  const PAGE_SIZE = 10;

  const convertToChartData = (data) => {
    const chartData = [];
    for (const key in data) {
      const values = [];
      for (const innerKey in data[key]) {
        values.push({
          label: innerKey,
          title: labelToTitle[innerKey] ? labelToTitle[innerKey] : innerKey,
          qty_buyers: data[key][innerKey],
        });
      }
      chartData[key] = values;
    }
    return chartData;
  };

  useEffect(() => {
    if (mainTab === "2") {
      getBuyerData(segmentedValue || null);
    }
  }, [mainTab, segmentedValue]);

  function getBuyerData(segmentedValue = null) {
    if (!segmentedValue) setBuyerLoading(true);
    if (segmentedValue) setSegmentedLoading(true);

    let segmentation = null;
    if (segmentedValue) {
      segmentation = {
        column: displayTab,
        value: segmentedValue,
      };
    }

    axiosClient
      .post(
        "/buyer/stats",
        {
          filter_values: filterValues,
          segmentation,
        },
        { withCredentials: true },
      )
      .then((response) => {
        if (!segmentedValue) {
          const chartData = convertToChartData(response.data.results);
          setBuyerGraphData(chartData);
          setBuyerLoading(false);
        } else {
          setSegmentedData(response.data.records);
          setSegmentedLoading(false);
        }
      });
  }

  function getChange(current, previous) {
    if (previous !== 0 && previous !== undefined) {
      const change = ((current - previous) / previous) * 100.0;
      return parseFloat(change.toFixed(2));
    }
    if ((current !== 0 && current !== undefined) || previous === 0) {
      return "inf";
    }
    return 0.0;
  }

  function mergeDicts(actual, compare, labelToTitle = {}) {
    const merged = {};

    for (const key in actual) {
      if (key in compare) {
        const compareLookup = Object.fromEntries(
          compare[key].map((item) => [item.label, item]),
        );

        merged[key] = actual[key].map((actualItem) => {
          const compareItem = compareLookup[actualItem.label];
          const mergedItem = { ...actualItem };

          // adicionar título
          mergedItem.title = labelToTitle[actualItem.label] || actualItem.label;

          if (compareItem) {
            for (const prop of METRICS) {
              mergedItem[`compare_${prop}`] = compareItem[prop];
              mergedItem[`variation_${prop}`] = getChange(
                actualItem[prop],
                compareItem[prop],
              );
            }
          } else {
            for (const prop of METRICS) {
              mergedItem[`compare_${prop}`] = null;
              mergedItem[`variation_${prop}`] = null;
            }
          }
          return mergedItem;
        });
      }
    }

    return merged;
  }

  useEffect(() => {
    if (analysisPeriod != "editCustom") {
      setLoading(true);
      setDateRanges(null);
      axiosClient
        .post(
          "/cart/stats",
          {
            analysisPeriod,
            customRange,
          },
          { withCredentials: true },
        )
        .then((response) => {
          const cartStatsDataActual = response.data.actual_data;
          const cartStatsDataCompare = response.data.compare_data;
          setDateRanges(response.data.date_ranges);
          setAggregatedData(
            mergeDicts(cartStatsDataActual, cartStatsDataCompare),
          );
          setLoading(false);
        });
    }
  }, [analysisPeriod]);

  const handleChangePage = (page) => {
    setCurrentPage(page);
  };

  function LineGraph({ data, activeTab }) {
    const chartRef = useRef();

    const option = {
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "line" },
        textStyle: {
          fontSize: 12,
          fontFamily: "'Open Sans', sans-serif",
        },
        formatter: function (params) {
          let name = params[0]?.axisValue || "";
          if (name.length > 30) name = name.slice(0, 30) + "...";

          const lines = params.map((p) => {
            const value = Array.isArray(p.value) ? p.value[0] : p.value;
            return `${p.marker} ${p.seriesName}: ${value ?? 0}`;
          });

          return `<strong>${name}</strong><br/>` + lines.join("<br/>");
        },
      },
      legend: {
        data: [statsLabels[displayMetric]],
        textStyle: {
          fontSize: 12,
          fontFamily: "'Open Sans', sans-serif",
        },
      },
      xAxis: {
        type: "category",
        data: data.map((i) => i.title),
        boundaryGap: false,
        axisLine: { show: true },
        axisLabel: {
          fontSize: 12,
          fontFamily: "'Open Sans', sans-serif",
          formatter: (value) =>
            value.length > 12 ? value.slice(0, 12) + "..." : value,
        },
      },
      yAxis: {
        type: "value",
        axisLabel: {
          fontSize: 12,
          fontFamily: "'Open Sans', sans-serif",
        },
        splitLine: {
          lineStyle: {
            type: "dashed",
            color: "#ccc",
          },
        },
      },
      series: [
        {
          name: statsLabels[displayMetric],
          type: "line",
          smooth: true,
          data: data.map((i) => i[displayMetric] || 0),
          symbol: "circle",
          symbolSize: 6,
          itemStyle: {
            color: token.colorPrimary,
          },
          lineStyle: {
            color: token.colorPrimary,
            width: 3,
          },
          areaStyle: {
            color: {
              type: "linear",
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: token.colorPrimary + "33" },
                { offset: 1, color: token.colorPrimary + "00" },
              ],
            },
          },
        },
      ],
      grid: {
        left: 40,
        right: 20,
        top: 50,
        bottom: 40,
      },
    };

    useEffect(() => {
      const timer = setTimeout(() => {
        if (chartRef.current) {
          chartRef.current.getEchartsInstance().resize();
        }
      }, 300);
      return () => clearTimeout(timer);
    }, [activeTab]);

    return (
      <ReactECharts
        ref={chartRef}
        option={option}
        style={{ height: 400, width: "100%" }}
      />
    );
  }
  function BarGraph({ data, activeTab }) {
    const chartRef = useRef();
    const slicedData = data
      .slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)
      .slice()
      .reverse();

    const hatchPattern = {
      type: "pattern",
      image: (() => {
        const canvas = document.createElement("canvas");
        canvas.width = 6;
        canvas.height = 6;
        const ctx = canvas.getContext("2d");
        ctx.strokeStyle = "#5b9bd5";
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(0, 6);
        ctx.lineTo(6, 0);
        ctx.stroke();
        return canvas;
      })(),
      repeat: "repeat",
    };

    const option = {
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
        position: function (point, params, dom, rect, size) {
          const viewWidth = size.viewSize[0];
          const boxWidth = size.contentSize[0];
          let x = point[0];
          const y = point[1];

          if (x + boxWidth > viewWidth) {
            x = x - boxWidth - 10;
            if (x < 0) x = 10;
          } else {
            x = x + 10;
          }

          return [x, y];
        },
        textStyle: {
          fontSize: 12,
          fontFamily: "'Open Sans', sans-serif",
        },
        formatter: function (params) {
          let name = params[0]?.axisValue || "";
          if (name.length > 30) name = name.slice(0, 30) + "...";

          const lines = params.map((p) => {
            const value = Array.isArray(p.value) ? p.value[0] : p.value;
            return `${p.marker} ${p.seriesName}: ${value ?? 0}`;
          });

          return `<strong>${name}</strong><br/>` + lines.join("<br/>");
        },
      },
      legend: { data: [statsLabels[displayMetric]] },
      xAxis: {
        type: "value",
        boundaryGap: [0, 0.05],
        axisLine: { show: true },
        splitLine: { show: false },
      },
      yAxis: {
        type: "category",
        data: slicedData.map((i) => i.title),
        axisLabel: { show: false },
        axisTick: { show: false },
        axisLine: { show: false },
      },
      series: [
        {
          name: statsLabels[displayMetric],
          type: "bar",
          data: slicedData.map((i) => i[displayMetric] || 0),
          itemStyle: { color: token.colorPrimary },
          label: {
            show: true,
            position: "insideLeft",
            formatter: ({ dataIndex }) => slicedData[dataIndex].title,
            color: "#fff",
            fontSize: 16,
            fontFamily: "'Open Sans', sans-serif",
            overflow: "truncate",
            textBorderColor: "#000",
            textBorderWidth: 2,
          },
          barCategoryGap: "10%",
          barMaxWidth: 50,
          barMinHeight: 30,
        },
      ],
    };

    if (data.some((i) => `compare_${displayMetric}` in i)) {
      option.legend.data.push(`${statsLabels[displayMetric]} (Comparação)`);
      option.series.push({
        name: `${statsLabels[displayMetric]} (Comparação)`,
        type: "bar",
        data: slicedData.map((i) => i[`compare_${displayMetric}`]),
        itemStyle: {
          color: hatchPattern,
          borderColor: token.colorPrimaryBgHover,
          borderWidth: 1,
        },
        label: { show: false },
        barCategoryGap: "10%",
        barMaxWidth: 50,
        barMinHeight: 30,
        barGap: "-100%",
      });
    }

    useEffect(() => {
      const timer = setTimeout(() => {
        if (chartRef.current) {
          chartRef.current.getEchartsInstance().resize();
        }
      }, 300);
      return () => clearTimeout(timer);
    }, [activeTab]);

    return (
      <>
        <ReactECharts
          ref={chartRef}
          option={option}
          style={{
            height: Math.max(400, slicedData.length * 40),
            width: "100%",
          }}
        />
        <Pagination
          current={currentPage}
          pageSize={PAGE_SIZE}
          total={data.length - 1}
          onChange={handleChangePage}
        />
      </>
    );
  }

  function DisplayGraph(props) {
    const sortedData = [...props.data].sort(
      (first, second) => second[displayMetric] - first[displayMetric],
    );

    const selectOptions = props.data.map((item) => ({
      label: `${item.title} (${item.qty_buyers})`,
      value: item.label,
    }));

    selectOptions.unshift({ label: "Todos", value: "all" });

    return graphType === "values" ? (
      <>
        <Title level={4}>
          Segmentação:{" "}
          <Select
            style={{ width: "300px" }}
            options={selectOptions}
            value={segmentedValue}
            onChange={(value) => setSegmentedValue(value)}
          />
        </Title>{" "}
        {segmentedLoading ? (
          <Skeleton active />
        ) : (
          segmentedData && (
            <Table
              pagination={{ showSizeChanger: true }}
              tableLayout="auto"
              scroll={{ x: "max-content" }}
              columns={buyerColumns}
              dataSource={segmentedData}
              showSorterTooltip={false}
            />
          )
        )}{" "}
      </>
    ) : graphType === "line" ? (
      <LineGraph data={props.data} />
    ) : graphType === "bar" ? (
      <BarGraph data={sortedData} />
    ) : (
      <Table
        scroll={{
          x: 800,
        }}
        showSorterTooltip={false}
        columns={columns}
        dataSource={sortedData}
      />
    );
  }

  const customerTabs = React.useMemo(
    () => [
      { key: "status", label: "Status do Cliente" },
      { key: "ABC", label: "Curva ABC" },
      { key: "registration_category", label: "Data de Cadastro" },
      { key: "num_orders_category", label: "Quantidade de Pedidos" },
      { key: "selleruser", label: "Representantes" },
      { key: "uf", label: "Estados" },
      { key: "municipio", label: "Cidades" },
      { key: "tipo_pessoa", label: "Tipo de Pessoa" },
    ],
    [],
  );

  const analysisTabs = React.useMemo(
    () => [
      { label: "Período", key: "week_date" },
      { label: "Representantes", key: "selleruser_id" },
      { label: "Estados", key: "uf" },
      { label: "Cidades", key: "municipio" },
      { label: "Clientes", key: "buyer_id" },
      { label: "Produtos", key: "product_id" },
      { label: "Métodos de Pagamento", key: "payment_info" },
      { label: "Métodos de Entrega", key: "shipping_info" },
      { label: "Emissor do Pedido", key: "who_bought" },
      { label: "Tipo de Pessoa", key: "tipo_pessoa" },
    ],
    [],
  );

  const buyerColumns = [
    {
      title: "Cliente",
      dataIndex: "buyer_name",
      key: "buyer_name",
      render: (text) => (
        <>
          <a>
            {text.split("(")[0].length > 20
              ? `${text.split("(")[0].substring(0, 20)}...`
              : text.split("(")[0]}
          </a>
          <br />
          {`(${text.split("(")[1]}`}
        </>
      ),
      sorter: (a, b) => a.buyer_name.localeCompare(b.buyer_name),
      fixed: screens.md ? "left" : false,
    },
    {
      title: "Curva ABC",
      dataIndex: "ABC",
      key: "ABC",
      sorter: (a, b) => a.ABC.localeCompare(b.ABC),
    },
    {
      title: "Status do Cliente",
      dataIndex: "status",
      key: "status",
      sorter: (a, b) => a.status.localeCompare(b.status),
    },
    {
      title: "Data de Registro",
      dataIndex: "registration_category",
      key: "registration_category",
      sorter: (a, b) =>
        a.registration_category.localeCompare(b.registration_category),
    },
    {
      title: "Vendedor",
      dataIndex: "selleruser",
      key: "selleruser",
      sorter: (a, b) => a.selleruser.localeCompare(b.selleruser),
    },
    {
      title: "Número de Pedidos",
      dataIndex: "num_orders_category",
      key: "num_orders_category",
      sorter: (a, b) =>
        a.num_orders_category.localeCompare(b.num_orders_category),
    },
    {
      title: "Dias Último Pedido",
      dataIndex: "days_since_last_order",
      key: "days_since_last_order",
      sorter: (a, b) => a.days_since_last_order - b.days_since_last_order,
    },
    {
      title: "Probabilidade",
      dataIndex: "probability",
      key: "probability",
      sorter: (a, b) => a.probability - b.probability,
    },
    {
      title: "Intervalo entre Pedidos",
      dataIndex: "timedelta",
      key: "timedelta",
      sorter: (a, b) => a.timedelta - b.timedelta,
    },
    {
      title: "Ticket Médio",
      dataIndex: "mean_ticket",
      key: "mean_ticket",
      sorter: (a, b) => a.mean_ticket - b.mean_ticket,
    },
  ];

  const columns = [
    {
      title: analysisTabs.find((tab) => tab.key === displayTab)?.label,
      dataIndex: "label",
      key: "label",
      render: (text, record) => (
        <Link
          onClick={() => {
            setOrderListModal(true);
            setHighLightData({ ...record, type: displayTab });
          }}
        >
          {text}
        </Link>
      ),
      ellipsis: true,
    },
    {
      title: "Número de Pedidos",
      dataIndex: "qty_orders",
      key: "qty_orders",
      sorter: true,
      sortOrder: displayMetric === "qty_orders" ? "descend" : null,
      sortDirections: ["descend"],
      onHeaderCell: (column) => ({
        onClick: () => {
          setDisplayMetric(column.dataIndex);
        },
      }),
    },
    {
      title: "Faturamento",
      dataIndex: "total_value",
      key: "total_value",
      sorter: true,
      sortOrder: displayMetric === "total_value" ? "descend" : null,
      sortDirections: ["descend"],
      onHeaderCell: (column) => ({
        onClick: () => {
          setDisplayMetric(column.dataIndex);
        },
      }),
    },
    {
      title: "Ticket Médio",
      dataIndex: "mean_ticket",
      key: "mean_ticket",
      sorter: true,
      sortOrder: displayMetric === "mean_ticket" ? "descend" : null,
      sortDirections: ["descend"],
      onHeaderCell: (column) => ({
        onClick: () => {
          setDisplayMetric(column.dataIndex);
        },
      }),
    },
    {
      title: "Clientes Positivados",
      dataIndex: "activations",
      key: "activations",
      sorter: true,
      sortOrder: displayMetric === "activations" ? "descend" : null,
      sortDirections: ["descend"],
      onHeaderCell: (column) => ({
        onClick: () => {
          setDisplayMetric(column.dataIndex);
        },
      }),
    },
    {
      title: "Mix Médio",
      dataIndex: "order_mix",
      key: "order_mix",
      sorter: true,
      sortOrder: displayMetric === "order_mix" ? "descend" : null,
      sortDirections: ["descend"],
      onHeaderCell: (column) => ({
        onClick: () => {
          setDisplayMetric(column.dataIndex);
        },
      }),
    },
    {
      title: "Preço Médio",
      dataIndex: "price",
      key: "price",
      sorter: true,
      sortOrder: displayMetric === "price" ? "descend" : null,
      sortDirections: ["descend"],
      onHeaderCell: (column) => ({
        onClick: () => {
          setDisplayMetric(column.dataIndex);
        },
      }),
    },
    {
      title: "Quantidade Média",
      dataIndex: "quantities",
      key: "quantities",
      sorter: true,
      sortOrder: displayMetric === "quantities" ? "descend" : null,
      sortDirections: ["descend"],
      onHeaderCell: (column) => ({
        onClick: () => {
          setDisplayMetric(column.dataIndex);
        },
      }),
    },
  ];

  const testOrders = [
    {
      key: "1",
      number: 1001,
      customer: "João Silva",
      city: "São Paulo",
      total: 250.5,
      date: "2026-01-31",
      gtin: "1234567890123",
    },
    {
      key: "2",
      number: 1002,
      customer: "Maria Oliveira",
      city: "Rio de Janeiro",
      total: 180.0,
      date: "2026-01-30",
      gtin: null,
    },
    {
      key: "3",
      number: 1003,
      customer: "Carlos Pereira",
      city: "Belo Horizonte",
      total: 320.75,
      date: "2026-01-29",
      gtin: "9876543210987",
    },
  ];

  const orderStats = {
    ordersCount: 3,
    revenue: 751.25,
    positiveCustomers: 3,
    avgTicket: 250.42,
    avgMix: 4,
    avgPrice: 200.42,
    avgQuantity: 2,
  };

  const orderListColumns = [
    { title: "Número", dataIndex: "number", key: "number" },
    { title: "Cliente", dataIndex: "customer", key: "customer" },
    { title: "Cidade", dataIndex: "city", key: "city" },
    {
      title: "Total",
      dataIndex: "total",
      key: "total",
      render: (value) => `R$ ${value.toFixed(2)}`,
    },
    { title: "Data", dataIndex: "date", key: "date" },
  ];

  return (
    <>
      <Tabs
        type="card"
        onChange={(tabValue) => {
          setMainTab(tabValue);
          if (tabValue == "2") {
            setDisplayTab("status");
            setDisplayMetric("qty_buyers");
            setGraphType("bar");
          } else {
            setDisplayTab("week_date");
            setDisplayMetric("qty_orders");
            setGraphType("line");
          }
        }}
        activeKey={mainTab}
        size="large"
        centered={screens.md}
        tabBarExtraContent={
          mainTab === "2" && (
            <Badge
              count={
                Object.values(filterValues).filter((value) => value !== "all")
                  .length
              }
            >
              <Button
                size={screens.md ? "large" : "middle"}
                onClick={() => setFilterDrawerVisible(true)}
                type="dashed"
                icon={<FilterTwoTone />}
              >
                Filtros
              </Button>
            </Badge>
          )
        }
      >
        <TabPane tab="Vendas" key="1">
          <Space
            orientation="vertical"
            size="small"
            style={{ display: "flex" }}
          >
            <Row
              gutter={{
                xs: 8,
                sm: 16,
                md: 24,
                lg: 32,
              }}
              justify="center"
              align="middle"
            >
              <Col>
                <Radio.Group
                  size={screens.md ? "middle" : "small"}
                  value={analysisPeriod}
                  onChange={(item) => {
                    item.target.value != "custom" &&
                      setAnalysisPeriod(item.target.value);
                  }}
                >
                  <Radio.Button value="today">Hoje</Radio.Button>
                  <Radio.Button value="last7">7 dias</Radio.Button>
                  <Radio.Button value="thisMonth">Mês atual</Radio.Button>
                  <Radio.Button
                    onClick={() => {
                      setDrawerVisible(~drawerVisible);
                      setAnalysisPeriod("editCustom");
                    }}
                    value="custom"
                  >
                    Personalizado
                  </Radio.Button>
                </Radio.Group>
              </Col>
              <Col>
                <Space size={2}>
                  <Tag color="blue">
                    {dateRanges ? (
                      `Período: ${dateRanges.period_eval.start.slice(0, 5)} - ${dateRanges.period_eval.end.slice(0, 5)}`
                    ) : (
                      <Skeleton.Input
                        active
                        size="small"
                        style={{ width: 56 }}
                      />
                    )}
                  </Tag>

                  <Tag>
                    {dateRanges && dateRanges.period_compare.start ? (
                      `Comparação: ${dateRanges.period_compare.start.slice(0, 5)} - ${dateRanges.period_compare.end.slice(0, 5)}`
                    ) : (
                      <Skeleton.Input
                        active
                        size="small"
                        style={{ width: 56 }}
                      />
                    )}
                  </Tag>
                </Space>
              </Col>
            </Row>
            {loading ? (
              <Skeleton active />
            ) : (
              <>
                <Tabs
                  activeKey={displayMetric}
                  onChange={setDisplayMetric}
                  tabBarGutter={8}
                >
                  {aggregatedData?.overall?.[0] &&
                    METRICS.map((key) => {
                      const value = aggregatedData.overall[0][key];
                      const variation =
                        aggregatedData.overall[0][`variation_${key}`];
                      const isCurrency = [
                        "total_value",
                        "mean_ticket",
                        "price",
                      ].includes(key);

                      const displayValue = isCurrency
                        ? parseFloat(value).toLocaleString("pt-BR", {
                            style: "currency",
                            currency: "BRL",
                          })
                        : value;

                      const displayVariation =
                        variation === "inf"
                          ? "inf"
                          : `${variation >= 0 ? "+" : ""}${parseFloat(variation).toFixed(2)}%`;

                      return (
                        <Tabs.TabPane
                          key={key}
                          tab={
                            <Card
                              hoverable
                              style={{
                                width: 200,
                                height: 90,
                                borderColor:
                                  displayMetric === key
                                    ? blue.primary
                                    : undefined,
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                textAlign: "center",
                              }}
                            >
                              <Space
                                size={4}
                                orientation="vertical"
                                style={{
                                  margin: 0,
                                  padding: 0,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                <Text type="secondary">{statsLabels[key]}</Text>
                                <Text
                                  style={{
                                    fontSize: 12,
                                    color:
                                      variation < 0 ? red.primary : green[7],
                                  }}
                                >
                                  {displayVariation}
                                </Text>
                                <Title
                                  style={{
                                    margin: 0,
                                    color: token.colorPrimary,
                                  }}
                                  level={4}
                                >
                                  {displayValue}
                                </Title>
                              </Space>
                            </Card>
                          }
                        />
                      );
                    })}
                </Tabs>
                <Tabs
                  tabPlacement="top"
                  defaultActiveKey="week_date"
                  activeKey={displayTab}
                  tabBarExtraContent={{
                    right: (
                      <Radio.Group
                        value={graphType}
                        size="small"
                        onChange={(e) => setGraphType(e.target.value)}
                      >
                        {displayTab === "week_date" && (
                          <Radio.Button value="line">Linha</Radio.Button>
                        )}
                        <Radio.Button value="bar">Barras</Radio.Button>
                        <Radio.Button value="table">Tabela</Radio.Button>
                      </Radio.Group>
                    ),
                  }}
                  onChange={(tabKey) => {
                    setCurrentPage(1);
                    setDisplayTab(tabKey);
                    setGraphType(
                      tabKey === "week_date"
                        ? "line"
                        : graphType === "line"
                          ? "bar"
                          : graphType,
                    );
                  }}
                >
                  {analysisTabs.map((tab) => (
                    <Tabs.TabPane key={tab.key} tab={tab.label}>
                      {aggregatedData && (
                        <DisplayGraph data={aggregatedData[tab.key]} />
                      )}
                    </Tabs.TabPane>
                  ))}
                </Tabs>
              </>
            )}
          </Space>
        </TabPane>
        <TabPane tab="Clientes" key="2">
          {buyerLoading ? (
            <Skeleton active />
          ) : (
            <>
              <Row gutter={24}>
                <Col
                  span={24}
                  sm={{ span: 12 }}
                  md={{ span: 8 }}
                  xl={{ span: 6 }}
                >
                  <Card
                    hoverable
                    style={{
                      margin: 4,
                      borderColor:
                        displayMetric == "qty_buyers" ? blue.primary : null,
                    }}
                    onClick={() => setDisplayMetric("qty_buyers")}
                  >
                    <Statistic
                      title="Quantidade de Clientes"
                      value={
                        buyerGraphData.ABC
                          ? buyerGraphData.ABC.reduce(
                              (acc, curr) => acc + curr.qty_buyers,
                              0,
                            )
                          : 0
                      }
                      valueStyle={{ color: blue.primary }}
                    />
                  </Card>
                </Col>
              </Row>
              <Tabs
                tabPosition="top"
                defaultActiveKey="status"
                activeKey={displayTab}
                tabBarExtraContent={{
                  right: (
                    <Radio.Group
                      value={graphType}
                      size="small"
                      onChange={(event) => setGraphType(event.target.value)}
                    >
                      <Radio.Button value="bar">Barras</Radio.Button>
                      <Radio.Button value="values">Dados</Radio.Button>
                    </Radio.Group>
                  ),
                }}
                onChange={(tabKey) => {
                  setSegmentedValue(null);
                  setGraphType("bar");
                  setSegmentedData(null);
                  setDisplayTab(tabKey);
                }}
              >
                {customerTabs.map((tab) => (
                  <Tabs.TabPane key={tab.key} tab={tab.label}>
                    {buyerGraphData && (
                      <DisplayGraph data={buyerGraphData[tab.key]} />
                    )}
                  </Tabs.TabPane>
                ))}
              </Tabs>
            </>
          )}
        </TabPane>
      </Tabs>
      <Drawer
        title="Selecionar data"
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
      >
        <Space
          orientation="vertical"
          size="middle"
          rootStyle={{ display: "flex" }}
        >
          <Row justify="center">
            <Col>
              <RangePicker
                onChange={(value) =>
                  setCustomRange(value.map((e) => e.format("DD/MM/YYYY")))
                }
                format="DD/MM/YYYY"
              />
            </Col>
          </Row>
          <Button
            block
            type="primary"
            onClick={() => {
              setDrawerVisible(false);
              setAnalysisPeriod("custom");
            }}
          >
            Salvar
          </Button>
        </Space>
      </Drawer>
      <Drawer
        title="Filtrar Dados"
        placement="right"
        onClose={() => setFilterDrawerVisible(false)}
        open={filterDrawerVisible}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={() => {
            getBuyerData();
            setFilterDrawerVisible(false);
          }}
          initialValues={filterValues}
        >
          <Divider>Clientes</Divider>
          <Form.Item
            name="customerOrder"
            label="Pedidos na plataforma"
            rules={[
              {
                required: true,
              },
            ]}
          >
            <Select
              onChange={(value) =>
                setFilterValues({ ...filterValues, customerOrder: value })
              }
              options={[
                {
                  value: "all",
                  label: "Todos",
                },
                {
                  value: "noOrder",
                  label: "Sem Pedidos",
                },
                {
                  value: "hasOrder",
                  label: "Com Pedidos",
                },
              ]}
            />
          </Form.Item>
          <Form.Item
            name="customerStatus"
            label="Status do cliente"
            rules={[
              {
                required: true,
              },
            ]}
          >
            <Select
              onChange={(value) =>
                setFilterValues({ ...filterValues, customerStatus: value })
              }
              options={[
                {
                  value: "all",
                  label: "Todos",
                },
                {
                  value: "served",
                  label: "Atendidos",
                },
                {
                  value: "rtb",
                  label: "Prontos para Comprar",
                },
                {
                  value: "risk",
                  label: "Em Risco",
                },
                {
                  value: "lost",
                  label: "Perdidos",
                },
              ]}
            />
          </Form.Item>
          <Form.Item
            name="abcCurve"
            label="Curva ABC"
            rules={[
              {
                required: true,
              },
            ]}
          >
            <Select
              onChange={(value) =>
                setFilterValues({ ...filterValues, abcCurve: value })
              }
              options={[
                {
                  value: "all",
                  label: "Todos",
                },
                {
                  value: "curveA",
                  label: "Curva A",
                },
                {
                  value: "curveB",
                  label: "Curva B",
                },
                {
                  value: "curveC",
                  label: "Curva C",
                },
              ]}
            />
          </Form.Item>
          <Form.Item
            name="registerDate"
            label="Data de cadastro"
            rules={[
              {
                required: true,
              },
            ]}
          >
            <Select
              onChange={(value) =>
                setFilterValues({ ...filterValues, registerDate: value })
              }
              options={[
                {
                  value: "all",
                  label: "Todos",
                },
                {
                  value: "today",
                  label: "Hoje",
                },
                {
                  value: "last7Days",
                  label: "Últimos 7 Dias",
                },
                {
                  value: "last30Days",
                  label: "Últimos 30 Dias",
                },
                {
                  value: "last12Months",
                  label: "Último ano",
                },
              ]}
            />
          </Form.Item>
          <Form.Item>
            <div style={{ marginTop: 30 }}>
              <Row justify="center" gutter={[30, 12]}>
                <Col xs={18} sm={8}>
                  <Button block type="primary" htmlType="submit">
                    Aplicar
                  </Button>
                </Col>
              </Row>
            </div>
          </Form.Item>
        </Form>
      </Drawer>
      <Modal
        title={`Pedidos do período ${highLightData?.title}`}
        open={orderListModal}
        onCancel={() => setOrderListModal(false)}
        footer={null}
        width="90%"
      >
        <Descriptions
          column={{ xs: 1, sm: 2, md: 4 }}
          layout="horizontal"
          size="small"
          bordered
          style={{ marginBottom: 12 }}
        >
          {[
            { label: "Pedidos", key: "qty_orders" },
            { label: "Faturamento", key: "total_value", isCurrency: true },
            { label: "Clientes", key: "activations" },
            { label: "Ticket", key: "mean_ticket", isCurrency: true },
            { label: "Mix", key: "order_mix" },
            { label: "Preço", key: "price", isCurrency: true },
            { label: "Qtd", key: "quantities" },
          ].map(({ label, key, isCurrency }) => {
            const variation = highLightData?.[`variation_${key}`];
            const value = highLightData?.[key];
            const displayValue = isCurrency
              ? parseFloat(value).toLocaleString("pt-BR", {
                  style: "currency",
                  currency: "BRL",
                })
              : value;
            const displayVariation =
              variation === "inf"
                ? "inf"
                : `${variation >= 0 ? "+" : ""}${variation?.toFixed(2)}%`;

            return (
              <Descriptions.Item key={key} label={label}>
                {displayValue}
                {variation != null && (
                  <Text
                    style={{ marginLeft: 4 }}
                    type={variation < 0 ? "danger" : "success"}
                  >
                    ({displayVariation})
                  </Text>
                )}
              </Descriptions.Item>
            );
          })}
        </Descriptions>

        {/* <Table
          columns={orderListColumns}
          size="small"
          dataSource={loading ? [] : testOrders}
          locale={{
            emptyText: loading ? <Skeleton active /> : <Empty />,
          }}
          pagination={{ pageSize: 5 }}
          rowKey="key"
          scroll={{ x: "max-content" }}
        /> */}
      </Modal>
    </>
  );
});

export default SellerStats;
